import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionHeader from '../components/ui/SectionHeader';
import QuoteBlock from '../components/ui/QuoteBlock';
import DecorativeBg from '../components/ui/DecorativeBg';
import SocialLink from '../components/ui/SocialLink';
import { homeApi, eventsApi, galleryApi } from '../api/api';
import { parseYoutubeId, youtubeThumb } from '../utils/youtube';
import { getUpcomingEvents, formatEventDateRange, formatEventDuration } from '../utils/events';
import { DEFAULT_SOCIAL_URLS, openSocialUrl } from '../utils/socialLinks';

function shuffleArray(list) {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

// Colors driven via inline styles so Tailwind CDN v2 (no orange-*) renders every card identically.
const OFFERING_STYLES = [
  {
    key: 'daily_prayers',
    emoji: '🕉️',
    cardStyle: { background: 'linear-gradient(to bottom right, #ffffff, #fff7ed)', borderColor: '#ffedd5' },
    cardHoverBorder: '#fdba74',
    iconStyle: { background: 'linear-gradient(to bottom right, #fed7aa, #fef08a)', borderColor: '#fdba74' },
    hoverOverlayStyle: { background: 'linear-gradient(to bottom right, rgba(255, 237, 213, 0.5), rgba(254, 249, 195, 0.5))' },
    badgeStyle: { color: '#ea580c', background: '#ffedd5' },
    titleHover: '#c2410c',
  },
  {
    key: 'spiritual_events',
    emoji: '🙏',
    cardStyle: { background: 'linear-gradient(to bottom right, #ffffff, #f0fdf4)', borderColor: '#dcfce7' },
    cardHoverBorder: '#86efac',
    iconStyle: { background: 'linear-gradient(to bottom right, #bbf7d0, #a7f3d0)', borderColor: '#86efac' },
    hoverOverlayStyle: { background: 'linear-gradient(to bottom right, rgba(220, 252, 231, 0.5), rgba(209, 250, 229, 0.5))' },
    badgeStyle: { color: '#16a34a', background: '#dcfce7' },
    titleHover: '#15803d',
  },
  {
    key: 'gau_seva',
    emoji: '🐄',
    cardStyle: { background: 'linear-gradient(to bottom right, #ffffff, #fefce8)', borderColor: '#fef9c3' },
    cardHoverBorder: '#fde047',
    iconStyle: { background: 'linear-gradient(to bottom right, #fef08a, #fed7aa)', borderColor: '#fde047' },
    hoverOverlayStyle: { background: 'linear-gradient(to bottom right, rgba(254, 249, 195, 0.5), rgba(255, 237, 213, 0.5))' },
    badgeStyle: { color: '#ca8a04', background: '#fef9c3' },
    titleHover: '#a16207',
  },
  {
    key: 'anna_seva',
    emoji: '🍛',
    cardStyle: { background: 'linear-gradient(to bottom right, #ffffff, #fef2f2)', borderColor: '#fee2e2' },
    cardHoverBorder: '#fca5a5',
    iconStyle: { background: 'linear-gradient(to bottom right, #fecaca, #fbcfe8)', borderColor: '#fca5a5' },
    hoverOverlayStyle: { background: 'linear-gradient(to bottom right, rgba(254, 226, 226, 0.5), rgba(252, 231, 243, 0.5))' },
    badgeStyle: { color: '#dc2626', background: '#fee2e2' },
    titleHover: '#b91c1c',
  },
  {
    key: 'seva_bhava',
    emoji: '🧹',
    cardStyle: { background: 'linear-gradient(to bottom right, #ffffff, #faf5ff)', borderColor: '#f3e8ff' },
    cardHoverBorder: '#d8b4fe',
    iconStyle: { background: 'linear-gradient(to bottom right, #e9d5ff, #c7d2fe)', borderColor: '#d8b4fe' },
    hoverOverlayStyle: { background: 'linear-gradient(to bottom right, rgba(243, 232, 255, 0.5), rgba(224, 231, 255, 0.5))' },
    badgeStyle: { color: '#9333ea', background: '#f3e8ff' },
    titleHover: '#7e22ce',
  },
];

const EVENT_CARD_STYLES = {
  orange: { border: 'border-orange-100', titleHover: '#ea580c', badgeBg: '#f97316' },
  green: { border: 'border-green-100', titleHover: '#16a34a', badgeBg: '#22c55e' },
  red: { border: 'border-red-100', titleHover: '#dc2626', badgeBg: '#ef4444' },
  purple: { border: 'border-purple-100', titleHover: '#9333ea', badgeBg: '#a855f7' },
  blue: { border: 'border-blue-100', titleHover: '#2563eb', badgeBg: '#3b82f6' },
  yellow: { border: 'border-yellow-100', titleHover: '#ca8a04', badgeBg: '#eab308' },
  indigo: { border: 'border-indigo-100', titleHover: '#4f46e5', badgeBg: '#6366f1' },
};

const DEFAULT_SOCIAL_MEDIA = [
  {
    id: 'youtube',
    contentKey: 'social_youtube',
    icon: 'fab fa-youtube',
    titleKey: 'home.social.youtube.title',
    descKey: 'home.social.youtube.desc',
    stats: [
      { value: '25K+', labelKey: 'home.social.youtube.stat_0_label' },
      { value: '150+', labelKey: 'home.social.youtube.stat_1_label' },
      { value: '2M+',  labelKey: 'home.social.youtube.stat_2_label' },
    ],
    btnKey: 'home.social.youtube.btn',
    btnClass: 'from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
    cardBorder: 'border-red-100',
    cardBg: 'from-red-50 to-red-100',
    iconBg: 'from-red-500 to-red-600',
    hoverTitle: 'group-hover:text-red-600',
    statColor: 'text-red-500',
    url: DEFAULT_SOCIAL_URLS.youtube,
  },
  {
    id: 'facebook',
    contentKey: 'social_facebook',
    icon: 'fab fa-facebook-f',
    titleKey: 'home.social.facebook.title',
    descKey: 'home.social.facebook.desc',
    stats: [
      { value: '15K+', labelKey: 'home.social.facebook.stat_0_label' },
      { value: 'Daily', labelKey: 'home.social.facebook.stat_1_label' },
    ],
    btnKey: 'home.social.facebook.btn',
    btnClass: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
    cardBorder: 'border-blue-100',
    cardBg: 'from-blue-50 to-blue-100',
    iconBg: 'from-blue-500 to-blue-600',
    hoverTitle: 'group-hover:text-blue-600',
    statColor: 'text-blue-500',
    url: DEFAULT_SOCIAL_URLS.facebook,
  },
  {
    id: 'instagram',
    contentKey: 'social_instagram',
    icon: 'fab fa-instagram',
    titleKey: 'home.social.instagram.title',
    descKey: 'home.social.instagram.desc',
    stats: [
      { value: '10K+', labelKey: 'home.social.instagram.stat_0_label' },
      { value: '1K+',  labelKey: 'home.social.instagram.stat_1_label' },
    ],
    btnKey: 'home.social.instagram.btn',
    btnClass: 'from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
    cardBorder: 'border-purple-100',
    cardBg: 'from-purple-50 to-pink-50',
    iconBg: 'from-purple-500 to-pink-500',
    hoverTitle: 'group-hover:text-purple-600',
    statColor: 'text-purple-500',
    url: DEFAULT_SOCIAL_URLS.instagram,
  },
];

function mergeSocialFromApi(apiData) {
  return DEFAULT_SOCIAL_MEDIA.map((base) => {
    const raw = apiData && apiData[base.contentKey];
    if (!raw || typeof raw !== 'object') return { ...base, stats: base.stats.map((s) => ({ ...s })) };
    const statsIn = Array.isArray(raw.stats) ? raw.stats : [];
    const stats = base.stats.map((fallback, idx) => {
      const fromArr = statsIn[idx] || {};
      const valueKey = `stat${idx + 1}Value`;
      return {
        value: (fromArr.value != null ? String(fromArr.value) : null)
          || (raw[valueKey] != null ? String(raw[valueKey]) : fallback.value),
        labelKey: fallback.labelKey,
      };
    });
    // YouTube may gain a 3rd Views stat even if older default had 2
    if (base.id === 'youtube' && statsIn[2] && stats.length < 3) {
      stats.push({
        value: String(statsIn[2].value != null ? statsIn[2].value : '2M+'),
        labelKey: 'home.social.youtube.stat_2_label',
      });
    }
    return {
      ...base,
      url: (raw.url != null && String(raw.url).trim()) ? String(raw.url).trim() : base.url,
      stats,
    };
  });
}

/** Parse "100K+", "1.5M", "500+", "15,000" → number; non-numeric → 0 */
function parseStatValue(raw) {
  const s = String(raw || '').trim().replace(/,/g, '').toUpperCase();
  if (!s) return 0;
  const m = s.match(/^(\d+(?:\.\d+)?)\s*([KMB])?\+?$/);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n)) return 0;
  const mult = { K: 1e3, M: 1e6, B: 1e9 }[m[2]] || 1;
  return n * mult;
}

function formatAudienceTotal(total) {
  if (!total || total <= 0) return '0+';
  return `${Math.round(total).toLocaleString('en-US')}+`;
}

/** Turn translation strings with \n into JSX with <br /> (HTML ignores plain \n) */
function titleWithBreaks(text) {
  return String(text || '')
    .split('\n')
    .filter((line) => line.length > 0)
    .map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}
        {i < arr.length - 1 ? <br /> : null}
      </React.Fragment>
    ));
}

const Home = () => {
  const { t, i18n } = useTranslation();

  // Build default slides using translation keys
  const buildDefaultSlides = (tFn) => [
    {
      bg: "linear-gradient(135deg, rgba(255,107,53,0.0) 0%, rgba(0,0,0,0.3) 100%), url('/temple-banner.png')",
      badge: { icon: 'fas fa-om', text: tFn('home.slides.0.badge') },
      title: titleWithBreaks(tFn('home.slides.0.title')),
    },
    {
      bg: "linear-gradient(135deg, rgba(255,107,53,0.0) 0%, rgba(0,0,0,0.3) 100%), url('/place-banner.png')",
      bgMobile: "linear-gradient(135deg, rgba(255,107,53,0.0) 0%, rgba(0,0,0,0.3) 100%), url('/place-banner-mobile.png')",
      badge: { icon: 'fas fa-spa', text: tFn('home.slides.1.badge') },
      title: titleWithBreaks(tFn('home.slides.1.title')),
    },
    {
      bg: "linear-gradient(135deg, rgba(255,107,53,0.0) 0%, rgba(0,0,0,0.3) 100%), url('/temple-banner-3.png')",
      bgMobile: "linear-gradient(135deg, rgba(255,107,53,0.0) 0%, rgba(0,0,0,0.3) 100%), url('/temple-banner-3-mobile.png')",
      badge: { icon: 'fas fa-dove', text: tFn('home.slides.2.badge') },
      title: titleWithBreaks(tFn('home.slides.2.title')),
    },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const photoStripRef = useRef(null);

  // Dynamic content from DB (falls back to defaults if API unavailable)
  const [slides,        setSlides]        = useState(() => buildDefaultSlides(t));
  const [announcements, setAnnouncements] = useState([]);
  const [welcomeVideoId, setWelcomeVideoId] = useState('EjqtlB1b02o');
  const [welcomeThumb, setWelcomeThumb] = useState('/welcome-msg.png');
  const [eventsData, setEventsData] = useState([]);
  const [liveStreamUrl, setLiveStreamUrl] = useState('');
  const [categoryCards, setCategoryCards] = useState([]);
  const [socialMedia, setSocialMedia] = useState(DEFAULT_SOCIAL_MEDIA);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );

  // Re-build default slides whenever language changes
  useEffect(() => {
    setSlides((prev) => {
      // Only reset if slides are still defaults (no DB override applied yet)
      return buildDefaultSlides(t);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    homeApi.getAll()
      .then(res => {
        if (res.success && res.data) {
          const d = res.data;
          // Map DB slider format to the component's slide format
          if (Array.isArray(d.slider) && d.slider.length > 0) {
            const mapped = d.slider.map((s) => ({
              bg: `linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%), url('${s.image}')`,
              badge: { icon: 'fas fa-om', text: s.badge || t('home.slides.0.badge') },
              title: [s.title || t('home.slides.0.title_1')],
            }));
            setSlides(mapped);
          }
          if (Array.isArray(d.announcements)) {
            setAnnouncements(d.announcements.filter(a => a.active));
          }
          const wv = d.welcome_video;
          if (wv) {
            const id = (typeof wv === 'object' ? (wv.videoId || parseYoutubeId(wv.url)) : parseYoutubeId(wv)) || '';
            if (id) setWelcomeVideoId(id);
            if (typeof wv === 'object' && (wv.thumbnail || '').trim()) {
              setWelcomeThumb(String(wv.thumbnail).trim());
            }
          }
          const ls = d.live_stream;
          const url = ls && (ls.url || '').trim() ? String(ls.url).trim() : '';
          setLiveStreamUrl(url);
          setSocialMedia(mergeSocialFromApi(d));
        }
      })
      .catch(() => { /* silently use defaults */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Featured gallery photos for home strip — shuffled on each page load; hide if none
  useEffect(() => {
    galleryApi
      .getAll()
      .then((res) => {
        if (!res.success || !Array.isArray(res.data)) {
          setCategoryCards([]);
          return;
        }
        const featured = res.data
          .filter((item) => item.featured === true || item.featured === 1 || item.featured === '1')
          .map((item) => {
            const img = (item.src || '').trim() || youtubeThumb(item.youtubeId);
            return img
              ? { id: item.id, img, title: item.title || 'Gallery' }
              : null;
          })
          .filter(Boolean);
        setCategoryCards(featured.length > 0 ? shuffleArray(featured) : []);
      })
      .catch(() => setCategoryCards([]));
  }, []);

  useEffect(() => {
    eventsApi
      .getAll()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setEventsData(res.data.map((e) => ({ ...e, image: e.imageUrl || e.image || '' })));
        }
      })
      .catch(() => { /* keep empty — hide cards */ });
  }, []);

  const upcomingEvents = useMemo(() => getUpcomingEvents(eventsData, 3), [eventsData]);

  const socialAudienceTotal = useMemo(() => {
    const sum = socialMedia.reduce((acc, sm) => {
      const first = sm.stats && sm.stats[0] ? sm.stats[0].value : '';
      return acc + parseStatValue(first);
    }, 0);
    return formatAudienceTotal(sum);
  }, [socialMedia]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide((index + slides.length) % slides.length);
  }, [slides.length]);

  const nextSlide = useCallback(() => goToSlide(currentSlide + 1), [currentSlide, goToSlide]);
  const prevSlide = useCallback(() => goToSlide(currentSlide - 1), [currentSlide, goToSlide]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  useEffect(() => {
    const container = videoContainerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            if (iframeRef.current && iframeRef.current.contentWindow) {
              try {
                iframeRef.current.contentWindow.postMessage(
                  JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
                  '*'
                );
              } catch (err) {
                // Ignore iframe message errors
              }
            }
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Photo strip: manual scroll only (arrows + swipe/wheel)
  const scrollPhotoStrip = useCallback((direction) => {
    const el = photoStripRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.min(320, el.clientWidth * 0.7), behavior: 'smooth' });
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('home.meta.title')}</title>
        <meta name="description" content={t('home.meta.description')} />
        <link rel="canonical" href="https://www.shreeramranujadham.org/" />
        <meta property="og:title" content={t('home.meta.title')} />
        <meta property="og:description" content={t('home.meta.description')} />
        <meta property="og:url" content="https://www.shreeramranujadham.org/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
      </Helmet>

      {/* ── Announcements Bar (shown only when there are active announcements) ── */}
      {announcements.length > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-4 text-center text-sm font-medium">
          <i className="fas fa-bullhorn mr-2 text-yellow-300"></i>
          {announcements.map((a, i) => (
            <span key={i}>
              {a.link ? (
                <a href={a.link} className="hover:underline text-yellow-100" target="_blank" rel="noreferrer">{a.text}</a>
              ) : (
                a.text
              )}
              {i < announcements.length - 1 && <span className="mx-3 opacity-60">|</span>}
            </span>
          ))}
        </div>
      )}

      {/* ── Hero Slider ─────────────────────────────────────── */}
      <section className="hero-slider relative overflow-hidden">
        {/* Floating decorative circles */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-400 bg-opacity-10 rounded-full animate-pulse z-10"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-yellow-400 bg-opacity-10 rounded-full animate-pulse z-10" style={{ animationDelay: '1s' }}></div>

        {slides.map((slide, i) => (
          <div
            key={i}
            className={`slide ${i === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: isMobile && slide.bgMobile ? slide.bgMobile : slide.bg }}
          >
            <div className="slide-content">
              <div className="text-center relative z-20">
                <div className="hero-badge inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 py-2 md:px-6 md:py-3 mb-4 md:mb-8">
                  <i className={`${slide.badge.icon} text-orange-400 mr-2 md:mr-3 text-base md:text-xl`}></i>
                  <span className="text-white font-medium text-sm md:text-base">{slide.badge.text}</span>
                </div>
                <h1 className="text-2xl md:text-7xl lg:text-8xl font-bold text-white mb-4 md:mb-8 leading-tight">
                  {slide.title}
                </h1>
              </div>
            </div>
          </div>
        ))}

        <button className="slider-btn prev" onClick={prevSlide} aria-label={t('home.slider.prev_aria')}>
          <i className="fas fa-chevron-left"></i>
        </button>
        <button className="slider-btn next" onClick={nextSlide} aria-label={t('home.slider.next_aria')}>
          <i className="fas fa-chevron-right"></i>
        </button>

        <div className="slider-indicators">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`indicator ${i === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(i)}
              aria-label={t('home.slider.indicator_aria', { n: i + 1 })}
            />
          ))}
        </div>
      </section>

      {/* ── Guru & Shishya Section (Bapu + Rutika Didi) ─────── */}
      <section className="py-12 md:py-24 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        <DecorativeBg />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              badge={{ icon: 'fas fa-spa', text: t('home.quote.section_badge') }}
              title={
                <>
                  {t('home.quote.section_title_1')}{' '}
                  <span className="text-orange-500 font-bold">{t('home.quote.section_title_2')}</span>
                </>
              }
              subtitle={t('home.quote.section_subtitle')}
              mb="mb-16"
            />

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {/* Bapu */}
              <div className="bg-white rounded-3xl shadow-xl border border-orange-100 p-8 hover:-translate-y-2 hover:shadow-2xl transition-all duration-500 flex flex-col items-center text-center">
                <div className="home-guide-photo-wrap mb-6">
                  <img
                    src={process.env.PUBLIC_URL + '/img-bapu.png'}
                    alt={t('home.quote.bapu.photo_alt')}
                    className="home-guide-photo rounded-3xl shadow-lg border-4 border-orange-100 object-cover"
                  />
                </div>
                <p className="text-orange-500 font-semibold text-base md:text-lg mb-1">
                  {t('home.quote.bapu.label')}
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                  {t('home.quote.bapu.name')}
                </h3>
                <p className="text-sm text-orange-600 font-medium">
                  {t('home.quote.bapu.role')}
                </p>
              </div>

              {/* Rutika Didi */}
              <div className="bg-white rounded-3xl shadow-xl border border-orange-100 p-8 hover:-translate-y-2 hover:shadow-2xl transition-all duration-500 flex flex-col items-center text-center">
                <div className="home-guide-photo-wrap mb-6">
                  <img
                    src={process.env.PUBLIC_URL + '/img-rutika.png'}
                    alt={t('home.quote.didi.photo_alt')}
                    className="home-guide-photo rounded-3xl shadow-lg border-4 border-orange-100 object-cover"
                  />
                </div>
                <p className="text-orange-500 font-semibold text-base md:text-lg mb-1">
                  {t('home.quote.didi.label')}
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                  {t('home.quote.didi.name')}
                </h3>
                <p className="text-sm text-orange-600 font-medium">
                  {t('home.quote.didi.role')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Welcome Section ──────────────────────────────────── */}
      <section className="pt-12 pb-8 md:py-24 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        <DecorativeBg />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="welcome-title text-center mb-8 leading-normal">
                <span className="block text-orange-500 font-semibold text-2xl md:text-4xl mb-1">
                  {t('home.welcome.label')}
                </span>
                <span className="block text-4xl md:text-6xl font-bold text-gray-800">
                  {t('home.welcome.title')}
                </span>
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
                {t('home.welcome.body')}
              </p>
            </div>

            <div className="mx-auto w-full" style={{ maxWidth: '960px' }}>
              {/* Welcome Video — 16:9 */}
              <div className="relative" ref={videoContainerRef}>
                <div className="welcome-video-frame relative overflow-hidden rounded-3xl shadow-2xl bg-black w-full">
                  {!isVideoPlaying ? (
                    <>
                      <picture>
                        <source
                          media="(min-width: 768px)"
                          srcSet={welcomeThumb || '/welcome-msg.png'}
                        />
                        <img
                          src="/welcome-msg-mobile.png"
                          alt="Video Cover"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </picture>
                      <button
                        type="button"
                        onClick={() => setIsVideoPlaying(true)}
                        aria-label={t('home.welcome.video_play_aria')}
                        className="absolute inset-0 w-full h-full cursor-pointer border-0 bg-transparent p-0 text-left"
                      >
                        <span className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none" aria-hidden="true"></span>
                        <span className="absolute bottom-4 left-4 md:bottom-6 md:left-6 flex items-center gap-3 md:gap-4 pointer-events-none">
                          <span className="group flex-shrink-0 w-14 h-14 md:w-16 md:h-16 bg-white bg-opacity-95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl">
                            <i className="fas fa-play text-orange-500 text-xl md:text-2xl ml-0.5"></i>
                          </span>
                          <span className="min-w-0">
                            <span className="block text-white font-bold text-base md:text-2xl leading-tight">{t('home.welcome.video_title')}</span>
                            <span className="block text-white text-opacity-90 text-xs md:text-base mt-0.5">{t('home.welcome.video_subtitle')}</span>
                          </span>
                        </span>
                      </button>
                    </>
                  ) : (
                    <>
                      <iframe
                        ref={iframeRef}
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${welcomeVideoId}?autoplay=1&enablejsapi=1&rel=0`}
                        title={t('home.welcome.video_title')}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      ></iframe>
                      <button
                        onClick={() => setIsVideoPlaying(false)}
                        className="absolute top-3 right-3 z-10 w-9 h-9 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
                        title={t('home.welcome.video_close')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Divine Seva & Dharma ─────────────────────────────── */}
      <section className="pt-12 pb-12 md:pb-24 bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 relative overflow-hidden">
        <DecorativeBg variant="warm" />

        <div className="w-full px-4 relative z-10">
          <SectionHeader
            badge={{ icon: '', text: t('home.offerings.section_badge') }}
            title={<>{t('home.offerings.section_title_1')} <span className="text-orange-600 font-bold">{t('home.offerings.section_title_2')}</span></>}
            subtitle={t('home.offerings.section_subtitle')}
            mb="mb-20"
            titleSize="text-3xl md:text-5xl lg:text-6xl"
          />
          <div className="text-center -mt-12 mb-16 text-orange-600 text-base font-medium">
            {t('home.offerings.dharma_tagline')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 w-full px-6 sm:px-12 md:px-20 lg:px-32 xl:px-40">
            {OFFERING_STYLES.map((item) => (
              <div
                key={item.key}
                className="group rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 hover:-translate-y-1 relative overflow-hidden"
                style={item.cardStyle}
                onMouseEnter={(e) => {
                  if (item.cardHoverBorder) e.currentTarget.style.borderColor = item.cardHoverBorder;
                  if (item.titleHover) {
                    const title = e.currentTarget.querySelector('h3');
                    if (title) title.style.color = item.titleHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (item.cardStyle?.borderColor) e.currentTarget.style.borderColor = item.cardStyle.borderColor;
                  if (item.titleHover) {
                    const title = e.currentTarget.querySelector('h3');
                    if (title) title.style.color = '';
                  }
                }}
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={item.hoverOverlayStyle}
                ></div>
                <div className="relative z-10">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300 border-2"
                    style={item.iconStyle}
                  >
                    <span className="text-3xl">{item.emoji}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 text-center transition-colors">
                    {t(`home.offerings.${item.key}.title`)}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-center text-sm">
                    {t(`home.offerings.${item.key}.desc`)}
                  </p>
                  <div className="mt-6 text-center">
                    <span className="font-medium text-xs px-3 py-1 rounded-full" style={item.badgeStyle}>
                      {t(`home.offerings.${item.key}.badge`)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Upcoming Events ──────────────────────────────────── */}
      {upcomingEvents.length > 0 && (
      <section className="pt-12 pb-12 md:pb-16 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        <DecorativeBg variant="subtle" />

        <div className="container mx-auto px-4 relative z-10">
          <SectionHeader
            badge={{ icon: 'fas fa-calendar-alt', text: t('home.events.section_badge') }}
            title={<>{t('home.events.section_title_1')} <span className="text-orange-500 font-bold">{t('home.events.section_title_2')}</span></>}
            subtitle={t('home.events.section_subtitle')}
            mb="mb-20"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-10 items-start">
            {upcomingEvents.map((ev) => {
              const style = EVENT_CARD_STYLES[ev.color] || EVENT_CARD_STYLES.orange;
              const typeLabel = ev.type === 'katha' ? t('home.events.type_katha') : t('home.events.type_event');
              const img = ev.imageUrl || ev.image || '';
              const openLive = ev.isLive && Boolean(liveStreamUrl);
              const openLiveStream = () => {
                if (!openLive) return;
                openSocialUrl(liveStreamUrl);
              };
              return (
                <div
                  key={ev.id}
                  className={`home-event-card group bg-white rounded-3xl shadow-xl overflow-hidden border ${style.border}${openLive ? ' home-event-card--live-link' : ''}`}
                  style={{ '--event-title-hover': style.titleHover }}
                  onClick={openLive ? openLiveStream : undefined}
                  onKeyDown={openLive ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openLiveStream();
                    }
                  } : undefined}
                  role={openLive ? 'link' : undefined}
                  tabIndex={openLive ? 0 : undefined}
                  aria-label={openLive ? t('home.events.watch_live_aria', { title: ev.title }) : undefined}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={img}
                      alt={ev.title}
                      className="w-full home-event-card-img object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60 pointer-events-none"></div>
                    <div className="absolute top-4 left-4 z-10 pointer-events-none">
                      <span className="home-event-chip home-event-chip--glass">
                        <span className="home-event-chip-label">{typeLabel}</span>
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 z-10 flex items-center justify-between gap-2 pointer-events-none">
                      {ev.isLive ? (
                        <span className="home-event-chip home-event-chip--live">
                          <span className="home-event-live-dot" aria-hidden="true" />
                          <span className="home-event-chip-label">{t('home.events.chip_live')}</span>
                        </span>
                      ) : null}
                      <div className="home-event-chip home-event-chip--glass home-event-chip--date flex-shrink-0 ml-auto">
                        <span className="home-event-chip-label">{formatEventDateRange(ev)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pt-5 pb-6">
                    <h3 className="home-event-card-title text-xl font-bold text-gray-800 mb-2">{ev.title}</h3>
                    {ev.location && (
                      <div className="flex items-center text-sm text-gray-600 mb-3">
                        <i className="fas fa-map-marker-alt mr-2 text-orange-500"></i>
                        <span>{ev.location}</span>
                      </div>
                    )}
                    <p className="text-gray-600 leading-relaxed home-event-card-desc">{ev.description}</p>
                    <div className="flex items-center justify-between gap-3">
                      {ev.isLive ? (
                        <button
                          type="button"
                          className="home-event-watch-live"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (liveStreamUrl) {
                              openSocialUrl(liveStreamUrl);
                            }
                          }}
                          disabled={!liveStreamUrl}
                        >
                          <i className="fas fa-play" aria-hidden="true"></i>
                          {t('home.events.watch_live')}
                        </button>
                      ) : (
                        <div className="flex items-center text-gray-500 text-sm min-w-0">
                          <i className="fas fa-clock mr-2 flex-shrink-0"></i>
                          <span className="truncate">{formatEventDuration(ev)}</span>
                        </div>
                      )}
                      {(ev.mapUrl || ev.location) && (
                        <a
                          href={ev.mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center flex-shrink-0 text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
                          style={{ color: '#2563eb', background: '#eff6ff' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="fas fa-directions mr-1.5"></i>
                          {t('home.events.directions')}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Link
              to="/events"
              className="group relative inline-flex items-center text-white px-12 py-5 text-lg font-bold transition-all duration-300 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-0.5 overflow-hidden"
              style={{ background: 'linear-gradient(to right, #f97316, #ef4444)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(to right, #ea580c, #dc2626)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(to right, #f97316, #ef4444)'; }}
            >
              <div className="relative z-10 flex items-center">
                <i className="fas fa-calendar-alt mr-4 text-xl group-hover:rotate-12 transition-transform duration-300"></i>
                <span className="tracking-wide">{t('home.events.view_all')}</span>
                <i className="fas fa-arrow-right ml-4 text-lg group-hover:translate-x-2 transition-transform duration-300"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* ── Portrait Photo Strip (featured gallery only) ───── */}
      {categoryCards.length > 0 && (
      <section className="pt-8 pb-16 md:pt-12 md:pb-24 bg-white">
        <div className="relative w-full">
          <button
            type="button"
            onClick={() => scrollPhotoStrip(-1)}
            aria-label={t('home.photo_strip.prev_aria')}
            className="absolute left-2 md:left-4 top-1/2 z-20 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black bg-opacity-30 hover:bg-opacity-50 text-white flex items-center justify-center transition-colors"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <button
            type="button"
            onClick={() => scrollPhotoStrip(1)}
            aria-label={t('home.photo_strip.next_aria')}
            className="absolute right-2 md:right-4 top-1/2 z-20 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black bg-opacity-30 hover:bg-opacity-50 text-white flex items-center justify-center transition-colors"
          >
            <i className="fas fa-chevron-right"></i>
          </button>

          <div
            ref={photoStripRef}
            className="overflow-x-auto scrollbar-hide px-4 md:px-12"
            style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
          >
            <div
              className={`flex flex-nowrap gap-3 md:gap-4 ${categoryCards.length <= 4 ? 'justify-center' : ''}`}
              style={{ width: categoryCards.length <= 4 ? '100%' : 'max-content' }}
            >
              {categoryCards.map((card, i) => (
                <div
                  key={card.id != null ? `feat-${card.id}` : `${card.title}-${i}`}
                  className="relative flex-shrink-0 overflow-hidden rounded-2xl shadow-md"
                  style={{ width: '300px', height: '450px' }}
                >
                  <img
                    src={card.img}
                    alt={card.title}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── Core Vision Section ──────────────────────────────── */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-8">
                  {t('home.vision.heading_1')}<br />
                  <span className="text-orange-500">{t('home.vision.heading_2')}</span>
                </h2>
                <QuoteBlock
                  quote={t('home.vision.sanskrit_quote')}
                  quoteSize="text-2xl md:text-3xl"
                  label={t('home.vision.quote_label')}
                  name={t('home.vision.quote_name')}
                />
                <p className="text-lg text-gray-600 mb-8">
                  {t('home.vision.translation')}
                </p>
              </div>
              <div>
                <img
                  src={process.env.PUBLIC_URL + '/temple-banner.png'}
                  alt={t('home.vision.temple_alt')}
                  className="w-full rounded-2xl shadow-2xl border-4 border-orange-100 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Spiritual Community / Social Media ───────────────── */}
      <section className="py-16 bg-white relative overflow-hidden">
        <DecorativeBg />

        <div className="container mx-auto px-4 relative z-10">
          <SectionHeader
            badge={{ icon: 'fas fa-users', text: t('home.social.section_badge') }}
            title={<>{t('home.social.section_title_1')}<br /><span className="text-orange-500 font-bold">{t('home.social.section_title_2')}</span></>}
            subtitle={t('home.social.section_subtitle')}
            mb="mb-20"
          />

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {socialMedia.map((sm) => (
              <div key={sm.id || sm.titleKey} className={`group bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border ${sm.cardBorder}`}>
                <div className={`relative overflow-hidden bg-gradient-to-br ${sm.cardBg} p-8`}>
                  <div className={`w-24 h-24 bg-gradient-to-br ${sm.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${sm.icon} text-white text-4xl`}></i>
                  </div>
                  <h3 className={`text-2xl font-bold text-gray-800 mb-4 text-center ${sm.hoverTitle} transition-colors`}>
                    {t(sm.titleKey)}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-center">{t(sm.descKey)}</p>
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center space-x-4 bg-white rounded-full px-6 py-3 shadow-md">
                      {sm.stats.map((s, j) => (
                        <React.Fragment key={j}>
                          {j > 0 && <div className="w-px h-8 bg-gray-200"></div>}
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${sm.statColor}`}>{s.value}</div>
                            <div className="text-sm text-gray-500">{t(s.labelKey)}</div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="text-center">
                    <SocialLink
                      href={sm.url}
                      className={`inline-flex items-center bg-gradient-to-r ${sm.btnClass} text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105`}
                    >
                      <i className={`${sm.icon} mr-3 text-xl`}></i>{t(sm.btnKey)}
                    </SocialLink>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-xl text-gray-600 font-medium">
              <i className="fas fa-users text-orange-500 mr-3"></i>
              {t('home.social.audience_label', { total: socialAudienceTotal })}
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
